import type { AgentReportResponse, InspectionReportPayload } from '../types/report';
import { createDefaultWorkspaceState, defaultBuyerShareState } from '../lib/defaultState';
import { getRecommendedNegotiationIssueNumbers } from '../lib/selectors';

export const sampleReportPayload: InspectionReportPayload = {
  summary: {
    inspector: 'Desert Peak Home Inspection',
    address: '123 Oak Street, Austin, TX 78701',
    propertyType: 'Single Family',
    squareFeet: 2450,
    yearBuilt: '2011',
    inspectionDate: '2026-01-10',
    totalIssues: 12,
    propertyOverview: 'A generally serviceable home with several plumbing, HVAC, exterior, and electrical items that merit repair planning before closing.',
    overallCondition: 'Negotiate before closing',
  },
  issueList: [
    {
      issueNumber: 1,
      title: 'Water Heater TPR Overflow Pipe Missing',
      description: 'The water heater temperature-pressure relief valve did not have a proper overflow pipe installed.',
      location: 'Garage',
      nspireLocation: 'Unit',
      systemCategory: 'plumbing',
      source: 'Inspector noted missing discharge piping at the water heater TPR valve.',
      fileRecco: 'Have a licensed plumber install a proper discharge pipe.',
      aiRecco: 'Treat as a safety repair before occupancy.',
      severity: { score: 'Severe', explanation: 'Missing discharge piping can expose occupants to scalding water or pressure discharge.' },
      safety: { concern: true, explanation: 'Improper TPR discharge is a plumbing safety concern.' },
      typical_for_age: { typical: false, explanation: 'This is not typical age-related wear.' },
      potential_consequences: 'Scalding injury, water damage, or unsafe pressure discharge.',
      repair_approach: 'Licensed plumber repair.',
      estimatedCost: { min: 300, max: 800 },
      recommendedTimeframe: 'Immediate',
      typicalBuyerAction: 'Request seller repair before closing.',
      specialist_evaluation_needed: true,
      specialist_type: 'Licensed plumber',
      derived: {
        normalizedSeverity: 'critical',
        normalizedTimeframe: 'immediate',
        estimatedCostMin: 300,
        estimatedCostMax: 800,
        estimatedCostAvg: 550,
        contextualThresholdCost: 800,
        safety: true,
        specialistNeeded: true,
        typicalForAge: false,
        categories: ['critical_safety'],
        tags: [{ name: 'urgent_repair', value: 'Urgent' }],
      },
    },
    {
      issueNumber: 2,
      title: 'HVAC Cooling Performance Below Expected Range',
      description: 'Temperature split was below the expected range during operation.',
      location: 'Attic air handler / exterior condenser',
      nspireLocation: 'Unit',
      systemCategory: 'hvac',
      source: 'Inspector reported limited cooling differential.',
      fileRecco: 'Have HVAC contractor evaluate and service system.',
      aiRecco: 'Get a licensed HVAC evaluation and quote before inspection period ends.',
      severity: { score: 'Moderate', explanation: 'System may need service or repair and could become a larger cost.' },
      safety: { concern: false, explanation: 'Performance issue without immediate life-safety indication.' },
      typical_for_age: { typical: true, explanation: 'Some performance degradation can occur with age and maintenance history.' },
      potential_consequences: 'Reduced cooling, higher utility costs, possible compressor or refrigerant repair.',
      repair_approach: 'HVAC evaluation and service.',
      estimatedCost: { min: 450, max: 2500 },
      recommendedTimeframe: '30 days',
      typicalBuyerAction: 'Request evaluation or credit.',
      specialist_evaluation_needed: true,
      specialist_type: 'HVAC contractor',
      derived: {
        normalizedSeverity: 'moderate',
        normalizedTimeframe: '30_days',
        estimatedCostMin: 450,
        estimatedCostMax: 2500,
        estimatedCostAvg: 1475,
        contextualThresholdCost: 1500,
        safety: false,
        specialistNeeded: true,
        typicalForAge: true,
        categories: ['specialist_review', 'negotiation_item'],
        tags: [{ name: 'specialist', value: 'Specialist' }],
      },
    },
    {
      issueNumber: 3,
      title: 'GFCI Protection Missing at Exterior Outlet',
      description: 'An exterior receptacle did not trip under GFCI testing.',
      location: 'Rear patio',
      nspireLocation: 'Outside',
      systemCategory: 'electrical',
      source: 'Inspector tested exterior receptacle and recommended correction.',
      fileRecco: 'Electrical contractor should repair or replace GFCI protection.',
      aiRecco: 'Prioritize before occupancy because exterior outlets are moisture exposed.',
      severity: { score: 'Severe', explanation: 'Missing GFCI protection at exterior wet-location outlet creates shock risk.' },
      safety: { concern: true, explanation: 'Electrical shock hazard in an exterior location.' },
      typical_for_age: { typical: false, explanation: 'Protection should function regardless of age.' },
      potential_consequences: 'Shock or electrocution risk.',
      repair_approach: 'Licensed electrician correction.',
      estimatedCost: { min: 150, max: 450 },
      recommendedTimeframe: 'Immediate',
      typicalBuyerAction: 'Request repair.',
      specialist_evaluation_needed: true,
      specialist_type: 'Electrician',
      derived: {
        normalizedSeverity: 'critical',
        normalizedTimeframe: 'immediate',
        estimatedCostMin: 150,
        estimatedCostMax: 450,
        estimatedCostAvg: 300,
        contextualThresholdCost: 800,
        safety: true,
        specialistNeeded: true,
        typicalForAge: false,
        categories: ['critical_safety'],
        tags: [{ name: 'urgent_repair', value: 'Urgent' }],
      },
    },
    {
      issueNumber: 4,
      title: 'Roof Flashing Sealant Deteriorated',
      description: 'Sealant around a roof penetration was cracked and weathered.',
      location: 'Roof',
      nspireLocation: 'Outside',
      systemCategory: 'roof',
      source: 'Inspector observed deteriorated sealant at flashing.',
      fileRecco: 'Have roofer reseal and inspect surrounding flashing.',
      aiRecco: 'Address during near-term maintenance to reduce leak risk.',
      severity: { score: 'Moderate', explanation: 'Water intrusion risk can escalate if left open to weather.' },
      safety: { concern: false, explanation: 'No immediate safety hazard reported.' },
      typical_for_age: { typical: true, explanation: 'Sealant wear is common with exposure.' },
      potential_consequences: 'Roof leak, sheathing damage, interior staining.',
      repair_approach: 'Roof maintenance repair.',
      estimatedCost: { min: 350, max: 1200 },
      recommendedTimeframe: '90 days',
      typicalBuyerAction: 'Request seller repair or budget for maintenance.',
      specialist_evaluation_needed: false,
      specialist_type: null,
      derived: {
        normalizedSeverity: 'moderate',
        normalizedTimeframe: '90_days',
        estimatedCostMin: 350,
        estimatedCostMax: 1200,
        estimatedCostAvg: 775,
        contextualThresholdCost: 1200,
        safety: false,
        specialistNeeded: false,
        typicalForAge: true,
        categories: ['water_intrusion_risk', 'maintenance'],
        tags: [{ name: 'water_risk', value: 'Water Risk' }],
      },
    },
    {
      issueNumber: 5,
      title: 'Exterior Grading Slopes Toward Foundation',
      description: 'Soil grading at one side of the home directs water toward the foundation.',
      location: 'Left exterior wall',
      nspireLocation: 'Outside',
      systemCategory: 'exteriorElements',
      source: 'Inspector recommended improving drainage away from structure.',
      fileRecco: 'Improve grading and downspout discharge.',
      aiRecco: 'Use as a negotiation or maintenance planning item depending on seller posture.',
      severity: { score: 'Moderate', explanation: 'Drainage can contribute to foundation movement or moisture intrusion.' },
      safety: { concern: false, explanation: 'No immediate safety hazard reported.' },
      typical_for_age: { typical: true, explanation: 'Exterior drainage often changes over time.' },
      potential_consequences: 'Foundation moisture, settlement risk, interior water intrusion.',
      repair_approach: 'Drainage correction and grading.',
      estimatedCost: { min: 800, max: 3500 },
      recommendedTimeframe: '90 days',
      typicalBuyerAction: 'Request credit or seller correction.',
      specialist_evaluation_needed: false,
      specialist_type: null,
      derived: {
        normalizedSeverity: 'moderate',
        normalizedTimeframe: '90_days',
        estimatedCostMin: 800,
        estimatedCostMax: 3500,
        estimatedCostAvg: 2150,
        contextualThresholdCost: 1500,
        safety: false,
        specialistNeeded: false,
        typicalForAge: true,
        categories: ['negotiation_item', 'water_intrusion_risk'],
        tags: [{ name: 'high_cost', value: 'High Cost' }],
      },
    },
    {
      issueNumber: 6,
      title: 'Dishwasher Drain Line Missing High Loop',
      description: 'The dishwasher drain line was not configured with a visible high loop.',
      location: 'Kitchen sink base',
      nspireLocation: 'Unit',
      systemCategory: 'appliances',
      source: 'Inspector noted drain line configuration under the sink.',
      fileRecco: 'Install proper high loop or air gap as locally required.',
      aiRecco: 'Minor repair; include with plumbing punch list.',
      severity: { score: 'Low', explanation: 'Low-cost correction to reduce backflow risk.' },
      safety: { concern: false, explanation: 'No immediate safety hazard reported.' },
      typical_for_age: { typical: false, explanation: 'Installation detail rather than aging.' },
      potential_consequences: 'Wastewater backflow or odor.',
      repair_approach: 'Handyman or plumber correction.',
      estimatedCost: { min: 75, max: 200 },
      recommendedTimeframe: '6 months',
      typicalBuyerAction: 'Bundle with repair list.',
      specialist_evaluation_needed: false,
      specialist_type: null,
      derived: {
        normalizedSeverity: 'minor',
        normalizedTimeframe: '6_months',
        estimatedCostMin: 75,
        estimatedCostMax: 200,
        estimatedCostAvg: 138,
        contextualThresholdCost: 800,
        safety: false,
        specialistNeeded: false,
        typicalForAge: false,
        categories: ['minor_repair'],
        tags: [{ name: 'diy_candidate', value: 'DIY' }],
      },
    },
  ],
  system_condition_summary: {
    plumbing: 'Water heater safety repair and dishwasher drain correction noted.',
    hvac: 'Cooling performance needs specialist evaluation.',
    electrical: 'Exterior GFCI correction should be prioritized.',
    roof: 'Localized flashing maintenance observed.',
    exteriorElements: 'Drainage improvements recommended.',
    appliances: 'Minor dishwasher installation detail noted.',
    structural: 'No explicit structural defect was extracted in this sample.',
    safetySystems: 'Safety-related findings are primarily electrical and plumbing.',
  },
  negotiation_insights: [
    'Lead with safety repairs and specialist evaluations.',
    'Use drainage and HVAC uncertainty as credit leverage.',
    'Bundle minor items into a seller punch list only if negotiation room remains.',
  ],
  recommended_further_evaluations: ['Licensed plumber', 'HVAC contractor', 'Electrician'],
  buyer_recommendations: [
    'Ask for safety repairs before closing.',
    'Request HVAC evaluation or seller credit.',
    'Budget for roof and drainage maintenance if seller concessions are limited.',
  ],
  rollups: {
    severityCounts: { critical: 2, moderate: 3, minor: 1, total: 6 },
    issueGroups: {
      criticalIssues: [1, 3],
      urgentIssues: [1, 2, 3],
      highStakesIssues: [2, 5],
      diyIssues: [6],
    },
    tagGroups: {
      urgent_repair: [1, 3],
      specialist: [2],
      high_cost: [5],
      diy_candidate: [6],
    },
    systemGroups: {
      plumbing: [1],
      hvac: [2],
      electrical: [3],
      roof: [4],
      exteriorElements: [5],
      appliances: [6],
    },
    systemCategories: [
      { systemId: 'plumbing', categories: ['critical_safety'], issueNumbers: [1] },
      { systemId: 'hvac', categories: ['specialist_review'], issueNumbers: [2] },
      { systemId: 'electrical', categories: ['critical_safety'], issueNumbers: [3] },
    ],
    costTotals: {
      minTotal: 2125,
      maxTotal: 8650,
      avgTotal: 5388,
    },
    validationErrors: [],
  },
};

const recommended = getRecommendedNegotiationIssueNumbers(sampleReportPayload);

export const sampleAgentReport: AgentReportResponse = {
  id: 'sample-report',
  reportKind: 'agent_created',
  role: 'agent',
  reportPayload: sampleReportPayload,
  workspaceState: createDefaultWorkspaceState(recommended),
  buyerShareState: defaultBuyerShareState,
  artifacts: {},
};
